import React, { FC, useEffect, useId, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useHttpMutation } from '@/api';
import { useQueryClient } from 'react-query';
import { API_URLS } from '../../consts';
import { LoggerService } from '@/shared/logger';
import { getStoreState } from '@/redux';
import {
  CelebrationFlow,
  CelebrationPhase,
  celebrationTimings,
  createCelebrationFlow,
} from './win-celebration-flow';
import './win-celebration.css';

export { celebrationTimings } from './win-celebration-flow';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

interface WinCelebrationProps {
  onComplete?: () => void;
}

interface ProgressionData {
  xp: number;
  levelName: string;
  levelEmoji: string;
  currentLevelMinXP: number;
  nextLevelMinXP: number;
}

interface GetCoinsResponse {
  balance: number;
  xpEarned?: number;
  leveledUp?: boolean;
  progression?: ProgressionData;
}

/** 0–100 progress through the current level, or null at the top level. */
export const levelProgressPercent = (p?: ProgressionData | null) => {
  if (!p || p.nextLevelMinXP === -1) return null;
  const span = p.nextLevelMinXP - p.currentLevelMinXP;
  if (span <= 0) return null;
  return Math.max(0, Math.min(100, ((p.xp - p.currentLevelMinXP) / span) * 100));
};

const continueLabel = (phase: CelebrationPhase) =>
  phase === 'presenting'
    ? 'Collect reward'
    : phase === 'claiming'
    ? 'Collecting…'
    : 'Continue';

export const WinCelebration: FC<WinCelebrationProps> = ({ onComplete }) => {
  const [phase, setPhase] = useState<CelebrationPhase>('presenting');
  const [result, setResult] = useState<GetCoinsResponse | null>(null);
  const queryClient = useQueryClient();
  const reduceMotion = useMediaQuery(REDUCED_MOTION_QUERY, { noSsr: true });
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const flowRef = useRef<CelebrationFlow | null>(null);

  const { mutate: claimCoins } = useHttpMutation<GetCoinsResponse>(
    API_URLS.CLAIM_COINS,
    'GET'
  );

  // Latest values for the once-per-celebration effect below (no stale
  // closure, no re-run that could claim twice).
  const latest = useRef({ claimCoins, onComplete, queryClient });
  latest.current = { claimCoins, onComplete, queryClient };

  // Move focus into the overlay so keyboard / screen-reader users are not
  // left on table controls hidden behind it; restore it afterwards.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      if (opener && document.contains(opener)) opener.focus?.();
    };
  }, []);

  useEffect(() => {
    const flow = createCelebrationFlow<GetCoinsResponse>({
      timings: celebrationTimings(reduceMotion),
      claim: ({ onSuccess, onError }) =>
        latest.current.claimCoins(undefined, {
          onSuccess: data => {
            LoggerService.info(`User won coins. New balance: ${data.balance}`);
            // The server has credited the win: refresh wallet + progression
            // even if the overlay has already been dismissed.
            const userID = getStoreState().userReducer.id;
            const qc = latest.current.queryClient;
            qc.invalidateQueries({
              queryKey: ['currentUserCoins', userID].join('-'),
            });
            qc.invalidateQueries({
              queryKey: ['userProgression', userID].join('-'),
            });
            onSuccess(data);
          },
          onError: err => {
            LoggerService.error('Error fetching coins after win:', err);
            onError(err);
          },
        }),
      onPhase: (next, data) => {
        setPhase(next);
        if (data) setResult(data);
      },
      onComplete: () => latest.current.onComplete?.(),
    });
    flowRef.current = flow;
    flow.start();
    return () => {
      flow.dispose();
      if (flowRef.current === flow) flowRef.current = null;
    };
    // Runs once per celebration; the claim must not repeat on re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      flowRef.current?.continueNow();
    } else if (e.key === 'Tab') {
      // single control: keep focus inside the dialog
      e.preventDefault();
      buttonRef.current?.focus();
    }
  };

  const progress = levelProgressPercent(result?.progression);
  const p = result?.progression;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      tabIndex={-1}
      data-testid="win-celebration"
      data-phase={phase}
      className="bh-win"
      onKeyDown={onKeyDown}
    >
      <div className="bh-win__card">
        <div className="bh-win__fan" aria-hidden="true">
          <span className="bh-win__mini" data-suit="♠">
            ♠
          </span>
          <span className="bh-win__mini bh-win__mini--red" data-suit="♥">
            ♥
          </span>
          <span className="bh-win__mini" data-suit="♣">
            ♣
          </span>
        </div>

        <p className="bh-win__eyebrow">Hand cleared</p>
        <h2 id={titleId} className="bh-win__title">
          You escaped!
        </h2>
        <p id={descId} className="bh-win__lead">
          No cards left, so you are safe from being the Bhabi.
        </p>

        <div
          className="bh-win__result"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {(phase === 'presenting' || phase === 'claiming') && (
            <p className="bh-win__pending">
              <span className="bh-win__spinner" aria-hidden="true" />
              {phase === 'claiming'
                ? 'Collecting your reward…'
                : 'Preparing your reward…'}
            </p>
          )}

          {phase === 'claimed' && result && (
            <>
              <dl className="bh-win__stats">
                <div>
                  <dt>Coin balance</dt>
                  <dd>
                    <span className="bh-win__coin" aria-hidden="true" />
                    {result.balance.toLocaleString()}
                  </dd>
                </div>
                {result.xpEarned != null && (
                  <div>
                    <dt>Experience</dt>
                    <dd>+{result.xpEarned} XP</dd>
                  </div>
                )}
              </dl>

              {result.leveledUp && p && (
                <p className="bh-win__level">
                  Level up! You are now{' '}
                  <span aria-hidden="true">{p.levelEmoji} </span>
                  <strong>{p.levelName}</strong>
                </p>
              )}

              {!result.leveledUp && p && progress != null && (
                <div className="bh-win__progress">
                  <div
                    className="bh-win__track"
                    role="progressbar"
                    aria-label={`Progress to the level after ${p.levelName}`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(progress)}
                  >
                    <span style={{ width: `${progress}%` }} />
                  </div>
                  <p className="bh-win__small">
                    {Math.max(0, p.nextLevelMinXP - p.xp)} XP to the next level
                  </p>
                </div>
              )}
            </>
          )}

          {phase === 'error' && (
            <p className="bh-win__error">
              We couldn&apos;t load your reward just now. Your coin balance
              will refresh when you are back home.
            </p>
          )}
        </div>

        <button
          ref={buttonRef}
          type="button"
          className="bh-win__btn"
          // aria-disabled (not disabled) so focus is not dropped mid-claim;
          // a press while claiming just closes once the claim settles.
          aria-disabled={phase === 'claiming'}
          aria-busy={phase === 'claiming'}
          onClick={() => flowRef.current?.continueNow()}
        >
          {continueLabel(phase)}
        </button>
      </div>
    </div>
  );
};
